import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gmDisplayName, gmInitial } from '../utils/displayName';

const OwnerNews = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterDays, setFilterDays] = useState(30);

  useEffect(() => {
    fetchOwners();
  }, [filterDays]);

  const fetchOwners = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users/public/owner-news');
      const data = await response.json();
      
      if (response.ok) {
        setUsers(data);
      } else {
        setError('Failed to load owner news');
      }
    } catch (err) {
      setError('Failed to load owner news');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRecentUsers = () => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - filterDays);
    
    return users.filter(user => 
      new Date(user.createdAt) >= cutoffDate && user.role !== 'admin'
    );
  };

  const getPendingOwners = () => {
    return users.filter(user => user.role === 'pending');
  };

  const getRecentAssignments = () => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - filterDays);
    
    return users.filter(user => 
      user.role === 'team_owner' && 
      user.teamId && 
      new Date(user.updatedAt) >= cutoffDate &&
      new Date(user.createdAt) < new Date(user.updatedAt)
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading owner news...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const recentUsers = getRecentUsers();
  const pendingOwners = getPendingOwners();
  const recentAssignments = getRecentAssignments();

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">👥 Owner News</h1>
        <p className="text-gray-600 dark:text-gray-400">Stay updated on team owner registrations and assignments</p>
      </div>

      {/* Time Filter */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilterDays(7)}
          className={`px-4 py-2 rounded ${
            filterDays === 7 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
          }`}
        >
          Last 7 Days
        </button>
        <button
          onClick={() => setFilterDays(30)}
          className={`px-4 py-2 rounded ${
            filterDays === 30 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
          }`}
        >
          Last 30 Days
        </button>
        <button
          onClick={() => setFilterDays(90)}
          className={`px-4 py-2 rounded ${
            filterDays === 90 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
          }`}
        >
          Last 90 Days
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Stats Cards */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <div className="text-4xl font-bold mb-2">{recentUsers.length}</div>
          <div className="text-green-100">New Registrations</div>
          <div className="text-sm text-green-100 mt-1">Last {filterDays} days</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <div className="text-4xl font-bold mb-2">{recentAssignments.length}</div>
          <div className="text-blue-100">Team Assignments</div>
          <div className="text-sm text-blue-100 mt-1">Last {filterDays} days</div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <div className="text-4xl font-bold mb-2">{pendingOwners.length}</div>
          <div className="text-yellow-100">Pending Owners</div>
          <div className="text-sm text-yellow-100 mt-1">Awaiting team assignment</div>
        </div>
      </div>

      {/* Recent Registrations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">✨ Recent Registrations</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">New team owners who joined in the last {filterDays} days</p>
        </div>
        <div className="p-6">
          {recentUsers.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No new registrations in this period</p>
          ) : (
            <div className="space-y-3">
              {recentUsers.map(user => (
                <Link
                  key={user.id}
                  to={user.role === 'team_owner' ? `/owner/${user.id}` : '#'}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded hover:bg-gray-100 dark:bg-gray-700 transition"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {gmInitial(user, { preferFirstName: true })}
                    </div>
                    <div>
                      <div className="font-semibold">{gmDisplayName(user, { preferFirstName: true })}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {user.gamertag && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">🎮 {user.gamertag}</span>
                        )}
                        {user.platform && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                            {user.platform === 'Xbox' ? '🎮' : '🎯'} {user.platform}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 dark:text-gray-400">{formatDate(user.createdAt)}</div>
                    {user.teamId ? (
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                        Team Assigned
                      </span>
                    ) : (
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
                        Pending Assignment
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Team Assignments */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">🏀 Recent Team Assignments</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Owners who were assigned to teams in the last {filterDays} days</p>
        </div>
        <div className="p-6">
          {recentAssignments.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No team assignments in this period</p>
          ) : (
            <div className="space-y-3">
              {recentAssignments.map(user => (
                <Link
                  key={user.id}
                  to={`/owner/${user.id}`}
                  className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:bg-blue-900/30 transition"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/200 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {gmInitial(user, { preferFirstName: true })}
                    </div>
                    <div>
                      <div className="font-semibold">{gmDisplayName(user, { preferFirstName: true })}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        {user.gamertag && <span>🎮 {user.gamertag}</span>}
                        {user.platform && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-200 text-blue-800">
                            {user.platform === 'Xbox' ? '🎮' : '🎯'} {user.platform}
                          </span>
                        )}
                      </div>
                      {user.team && (
                        <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mt-1">
                          📍 {user.team.city} {user.team.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 dark:text-gray-400">{formatDate(user.updatedAt)}</div>
                    <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      Team Assigned
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pending Owners */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">⏳ Pending Owners</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Registered users waiting for team assignment</p>
        </div>
        <div className="p-6">
          {pendingOwners.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No pending owners</p>
          ) : (
            <div className="space-y-3">
              {pendingOwners.map(user => (
                <div key={user.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded hover:bg-yellow-100 dark:bg-yellow-900/30 transition">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {gmInitial(user, { preferFirstName: true })}
                    </div>
                    <div>
                      <div className="font-semibold">{gmDisplayName(user, { preferFirstName: true })}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {user.gamertag && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">🎮 {user.gamertag}</span>
                        )}
                        {user.platform && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-200 text-yellow-800">
                            {user.platform === 'Xbox' ? '🎮' : '🎯'} {user.platform}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Registered {formatDate(user.createdAt)}</div>
                    <span className="inline-block mt-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded">
                      Awaiting Team
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerNews;
